import posthog from 'posthog-js';
import { BETA_KEYS } from '@/constants';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';
import toggleBeta from './toggle-beta';

interface VersionArea {
    /** PostHog event prefix, e.g. `compose-version.to-classic.completed`. */
    eventPrefix: string;
    /** Value for the shared switch-back survey's `switch_area` property. */
    switchArea: string;
    /** Where the new version lives, for callers that navigate rather than update in place. */
    newVersionPath: string;
    /** Person property stamped with the active version, where it's worth tracking per user. */
    personProperty?: { key: string; newValue: string; classicValue: string };
}

/** Everything that differs between the two version switches, in one place. */
export const VERSION_AREAS: Record<string, VersionArea> = {
    [BETA_KEYS.COMPOSE_BETA]: {
        eventPrefix: 'compose-version',
        switchArea: 'compose',
        newVersionPath: '/posts/calendar',
        personProperty: { key: 'composeVersion', newValue: 'fenix', classicValue: 'legacy' },
    },
    [BETA_KEYS.REPORTS_BETA]: {
        eventPrefix: 'insights-version',
        switchArea: 'insights',
        newVersionPath: '/dashboards',
    },
};

function capture(betaKey: string, event: string, properties?: Record<string, unknown>) {
    if (!POSTHOG_ENABLED) return;
    const area = VERSION_AREAS[betaKey];
    // send_instantly so a caller that navigates straight after doesn't drop the batched event.
    posthog.capture(`${area.eventPrefix}.${event}`, properties, { send_instantly: true });
}

function stampPersonProperty(betaKey: string, toNew: boolean) {
    if (!POSTHOG_ENABLED) return;
    const property = VERSION_AREAS[betaKey].personProperty;
    if (!property) return;
    posthog.capture(
        '$set',
        { $set: { [property.key]: toNew ? property.newValue : property.classicValue } },
        { send_instantly: true },
    );
}

/**
 * Moves this browser onto the new version. Deliberately frictionless: going back to a classic
 * version is gated behind a survey, this direction never is (DEV-4478).
 *
 * Pass `null` as the destination to update in place - callers rendered inside the settings page
 * stay where they are, since `useEnabledBetas` re-renders them. Callers elsewhere (the header
 * chip) navigate, because the page they're on belongs to the version they just left.
 */
export function switchToNewVersion(
    betaKey: string,
    destination: string | null = VERSION_AREAS[betaKey]?.newVersionPath,
) {
    toggleBeta(betaKey, true);
    capture(betaKey, 'to-new.completed');
    stampPersonProperty(betaKey, true);

    if (destination) {
        window.location.href = destination;
    }
}

/**
 * Moves this browser back onto the classic version. Only ever called once the switch-back survey
 * has been submitted (or skipped because it could not be shown) - see `useSurveyGatedAction`.
 */
export function switchToClassicVersion(betaKey: string, surveyShown: boolean) {
    toggleBeta(betaKey, false);
    capture(betaKey, 'to-classic.completed', { survey_shown: surveyShown });
    stampPersonProperty(betaKey, false);
}

/** Records that the user asked to go back, before the survey opens. Funnel numerator. */
export function captureSwitchToClassicRequested(betaKey: string) {
    capture(betaKey, 'to-classic.requested');
}

/** Records that the row was rendered at all. Funnel denominator. */
export function captureVersionSettingShown(betaKey: string, isUsingNew: boolean) {
    capture(betaKey, 'settings-row-shown', { current_version: isUsingNew ? 'new' : 'classic' });
}
